import { Link } from "@tanstack/react-router";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useParams } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { discussionsList, discussionsReadRoute } from "app/router";
import {
    Button,
    Card,
    CardTitle,
    NothingFound,
    TablePagination,
    TableSearch,
} from "components/elements";
import { OrganizationSelector } from "components/form/OrganizationSelector";
import { useAllOrganizations } from "hooks/useAllOrganizations";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDiscussions } from "hooks/useDiscussions";
import { useThrottled } from "hooks/useThrottled";
import { trpc } from "lib/trpc";
import { ClientDocument, DiscussionCreate } from "@compass/shared/types";
import { CreateDiscussionModal } from "./CreateDiscussionModal";
import { DiscussionsListTable } from "./DiscussionsListTable";
import { NoDiscussions } from "./NoDiscussions";

export const DiscussionsList = () => {
    const { enqueueSnackbar } = useSnackbar();
    const currentUser = useCurrentUser();
    const organizationsList = useAllOrganizations(currentUser);
    const input = useSearch({ from: discussionsList.id });

    const navigate = useNavigate();
    const createDiscussion = trpc.discussions.create.useMutation();
    const utils = trpc.useUtils();
    const throttledNavigate = useThrottled(navigate);
    const [isCreateDiscussionModalOpen, setCreateDiscussionModalOpen] = useState(false);
    const [createDiscussionattachments, setcreateDiscussionattachments] = useState<
        ClientDocument[]
    >([]);
    const [searchOrganization, setSearchOrganization] = useState<number | null | undefined>(
        input.organizationId ?? undefined,
    );

    const [searchValue, setSearchValue] = useState<string>(input.search ?? "");

    const { data, isInitialLoading, error, nothingFound } = useDiscussions(input);

    const getPageForId = trpc.discussions.getPageForId.useQuery(
        { id: input.id as number, pageSize: 10 },
        { enabled: !!input.id && input.page === undefined },
    );

    useEffect(() => {
        if (getPageForId.data?.page !== undefined) {
            navigate({
                search: { ...input, page: getPageForId.data.page },
                replace: true,
            });
        }
    }, [getPageForId.data?.page]);

    const onChangePage = (page: number | undefined) => {
        navigate({ search: { ...input, page }, replace: true });
    };

    const onOrganizationChange = (value: number | null | undefined) => {
        setSearchOrganization(value);
        throttledNavigate({
            search: { ...input, organizationId: value ? value : undefined },
            replace: true,
        });
    };

    const onSearchChange = (search: string) => {
        setSearchValue(search);
        throttledNavigate({
            search: { ...input, search: search === "" ? undefined : search },
            replace: true,
        });
    };

    const onClearFilters = () => {
        setSearchValue("");
        navigate({ search: {}, replace: true });
    };

    const handleCreateSubmit = (discussion: DiscussionCreate) => {
        const dataWithAttachments = {
            ...discussion,
            message: {
                ...discussion.message,
                attachments: createDiscussionattachments.map((a) => a.id),
            },
        };

        createDiscussion.mutate(dataWithAttachments, {
            onSuccess: () => {
                utils.discussions.list.invalidate();
                setCreateDiscussionModalOpen(false);
            },
            onError: (e) => {
                console.error(e);
                enqueueSnackbar(`${e}`, { variant: "error" });
            },
        });
    };

    return (
        <div className="mx-auto flex h-screen w-full flex-col pt-6">
            <div className=""></div>
            <div className="flex flex-wrap items-baseline justify-between gap-4 ">
                <div className="font-sans text-[28px] font-semibold text-[#424242]">
                    Discussions
                </div>

                <div className="ml-auto flex flex-wrap items-center gap-4">
                    {currentUser?.isCompassUser && (
                        <div className="flex flex-row gap-2 ">
                            <OrganizationSelector
                                inputClassName="h-9 w-full"
                                withClear
                                value={searchOrganization}
                                onChange={onOrganizationChange}
                                organizations={organizationsList?.data ?? []}
                                placeholder="Select organization"
                            />

                            <TableSearch value={searchValue} onChange={onSearchChange} />
                        </div>
                    )}

                    <Button onClick={() => setCreateDiscussionModalOpen(true)} variant="primary">
                        New Discussion
                    </Button>
                </div>
            </div>

            <div className="my-4 flex flex-1 flex-col gap-4 ">
                {nothingFound ? <NothingFound onClearFilters={onClearFilters} /> : null}

                {isInitialLoading && <div>Loading...</div>}

                {data && data.total === 0 && (
                    <NoDiscussions setModalOpen={setCreateDiscussionModalOpen} />
                )}

                {data && data.total > 0 && (
                    <div className="mb-4 flex flex-col gap-4">
                        <div className="">
                            <DiscussionsListTable
                                discussions={data?.discussions || []}
                                selectedId={input.id}
                            />
                        </div>

                        <TablePagination
                            total={data?.totalFound}
                            page={input.page}
                            onChangePage={onChangePage}
                        />
                    </div>
                )}
            </div>

            {currentUser && (
                <CreateDiscussionModal
                    isOpen={isCreateDiscussionModalOpen}
                    onClose={setCreateDiscussionModalOpen}
                    onSubmit={handleCreateSubmit}
                    attachments={createDiscussionattachments}
                    setAttachments={setcreateDiscussionattachments}
                    currentUser={currentUser}
                    organizations={organizationsList?.data ?? []}
                />
            )}
        </div>
    );
};
